import Utilities from './utilities';
import * as IntegratorAPI from './integrator_requests';

class Integrator {
    /**
     * @param {string} baseUrl
     * @param {Object} baseData
     * @param {Logger} logger
     */
    constructor(baseUrl, baseData, logger, pageContext = '') {
        this.baseUrl = baseUrl;
        this.baseData = baseData;
        this.logger = logger;
        this.pageContext = pageContext || '';

        this.requestData = Utilities.getAllQueryParams();
    }

    attachPageContext(data, verb) {
        const requestData = { ...data };

        if (!this.pageContext) {
            return requestData;
        }

        if (verb === 'GET') {
            return {
                ...requestData,
                pageContext: this.pageContext,
            };
        }

        return {
            ...requestData,
            pageContext: this.pageContext,
        };
    }

    /**
     * Add data
     * @param {Object} data
     */
    appendBaseData(data) {
        this.baseData = Object.assign(this.baseData, data);
    }

    /**
     * Perform an initialisation call
     * @param {Object} data
     * @param {function} onSuccess
     * @param {function} onError
     */
    initRenderedRequest(data, onSuccess, onError) {
        const payload = Object.assign({}, this.baseData, data);
        this.makeRequest('POST', 'lp/init/rendered', payload, onSuccess, onError);
    }

    /**
     * Creates an iframe and loads a URL in it
     * @param {Object} data
     */
    heIframeIdentify(data) {
        const payload = Object.assign({}, this.baseData, data);
        this.loadIframe('identify-frame', 'lp/he/identify', payload);
    }

    /**
     * Creates an iframe and loads the supplied URL and endpoint in it.
     * This is for situations when there is a separate integrator and HE service
     * @param {string} url
     * @param {string} endpoint
     * @param {Object} data
     */
    customHeIframeIdentify(url, endpoint, data) {
        const payload = Object.assign({}, this.baseData, data);
        this.loadUrlIframe('identify-frame', url, endpoint, payload);
    }

    customHeIframeIdentifyNoBaseData(url, endpoint, data) {
        this.loadUrlIframe('identify-frame', url, endpoint, data);
    }

    /**
     * Creates an iframe and loads the supplied URL and endpoint in it.
     * This is for situations when there is a separate integrator and HE service
     * Uses integrator base url
     * @param {string} endpoint
     * @param {Object} data
     */
    customHeIframeIdentifyBaseUrl(endpoint, data) {
        const payload = Object.assign({}, this.baseData, data);
        this.loadUrlIframe('identify-frame', this.baseUrl, endpoint, payload);
    }

    /**
     * Creates an iframe and loads an URL in it
     * @param {Object} data
     */
    heIframeSubscribe(data) {
        const payload = Object.assign({}, this.baseData, data);
        this.loadIframe('subscribe-frame', 'lp/he/subscribe', payload);
    }

    /**
     * Perform a GET identify request
     * @param {Object} data
     * @param {string} endpoint
     * @param {string} bearer
     * @param {function} onSuccess
     * @param {function} onError
     */
    customHeIdentifyGetRequestWithAuth(data, endpoint, bearer, onSuccess, onError) {
        const payload = Object.assign({}, this.baseData, data);
        this.makeRequestWithAuth('GET', endpoint, bearer, payload, onSuccess, onError);
    }

    LPSubscribeRequest(data, endpoint, onSuccess, onError, timeout = 40000) {
        const payload = Object.assign({}, this.baseData, data);
        this.makeRequestToLP('POST', 'integrator/subscribe/' + endpoint, payload, onSuccess, onError, timeout);
    }

    LPOtpSubscribeCustomRequest(data, endpoint, onSuccess, onError) {
        const payload = Object.assign({}, this.baseData, data);
        this.makeRequestToLP('POST', 'integrator/subscribe/' + endpoint, payload, onSuccess, onError);
    }

    customCancelRequest(data, endpoint, onSuccess, onError) {
        const payload = Object.assign({}, this.baseData, data);
        this.makeRequestCancel('POST', endpoint, payload, onSuccess, onError);
    }

    validateUsernameRequest(data, onSuccess, onError) {
        const payload = Object.assign({}, this.baseData, data);
        this.makeRequestToLP('POST', "integrator/username_validate", payload, onSuccess, onError);
    }

    changeUsernameRequest(data, onSuccess, onError) {
        const payload = Object.assign({}, this.baseData, data);
        this.makeRequestToLP('POST', "integrator/username_change", payload, onSuccess, onError);
    }

    /**
     * Checks if a given identity has a subscription
     * @param {Object} data
     * @param {function} onSuccess
     * @param {function} onError
     */
    LPCheckSubscriptionRequest(data, onSuccess, onError) {
        const payload = Object.assign({}, this.baseData, data);
        // calls the integrator controller instead of actual integrator
        this.makeRequestToLP('GET', 'integrator/check', payload, onSuccess, onError)
    }

    LPOtpGenerateCustomRequest(data, endpoint, onSuccess, onError = false) {
        const payload = Object.assign({}, this.baseData, data);
        this.makeRequestToLP('POST', 'integrator/generate/' + endpoint, payload, onSuccess, onError);
    }

    /**
     * Sends a request to the integrator
     *
     * @param {string}   type      Type of request (GET or POST)
     * @param {string}   endpoint  API endpoint (e.g. 'lp/init/rendered')
     * @param {Object}   data      Object of data to be sent
     * @param {function} onSuccess Callback to be called when request is successful
     * @param {function} onError   Callback for when an error occurs
     * @param internal
     */
    makeRequestWithAuth(type, url, auth, data, onSuccess, onError, internal = false) {
        this.logger.debug(`Integrator: preparing request to ${url} with verb ${type}`);

        const xhr = new XMLHttpRequest();
        const verb = type === 'GET' ? 'GET' : 'POST';
        const path = url;
        const requestData = data;
        xhr.timeout = 60000;

        const requestBody = null;
        const queryString = "";

        this.logger.debug(`Integrator: making Request ${verb} ${path}${queryString}`, { "request": requestData });

        xhr.open(verb, path + queryString);
        xhr.setRequestHeader('Content-Type', 'application/json');
        xhr.setRequestHeader('X-Source-System', 'he-partner');
        xhr.setRequestHeader('x-messageid', '1234');
        xhr.setRequestHeader('X-App', 'he-partner');
        xhr.setRequestHeader('Authorization', 'Bearer ' + auth);
        // xhr.setRequestHeader('Access-Control-Allow-Origin', '*');
        xhr.onerror = () => {
            this.logger.error(`Integrator: got ERROR calling  ${verb} ${path}`, { "response": xhr.response });
            let payload = {};
            try {
                payload = JSON.parse(xhr.response);
                payload.response_string = xhr.response;
            } catch (error) {
                payload = { reference: null, status_code: xhr.status, message: 'could not parse integrator response', response_string: xhr.response, error: error };
            }
            onError(payload);
        };
        xhr.ontimeout = () => {
            this.logger.error(`Integrator: got TIMEOUT calling  ${verb} ${path}`, requestData);
            onError({ reference: null, message: 'timeout' });
        };
        xhr.onreadystatechange = () => {
            // if request is done
            if (xhr.readyState === XMLHttpRequest.DONE) {
                const xhrStatus = xhr.status;
                if (xhrStatus === 200) {
                    this.logger.debug(`Integrator: received response  ${verb} ${path}`, { "response": xhr.responseText });
                    onSuccess(JSON.parse(xhr.response));
                } else {
                    let payload = {};
                    try {
                        payload = JSON.parse(xhr.response);
                        payload.response_string = xhr.responseText;
                    } catch (error) {
                        payload = { reference: null, status_code: xhrStatus, message: 'could not parse integrator response', response_string: xhr.responseText, error: error };
                    }
                    onError(payload);
                }
            }
        };

        xhr.send(requestBody);
    }

    /**
     * Sends a request to the integrator
     *
     * @param {string}   type      Type of request (GET or POST)
     * @param {string}   endpoint  API endpoint (e.g. 'lp/init/rendered')
     * @param {Object}   data      Object of data to be sent
     * @param {function} onSuccess Callback to be called when request is successful
     * @param {function} onError   Callback for when an error occurs
     * @param {number}   timeout   Timeout in milliseconds (default 40000)
     */
    makeRequestToLP(type, endpoint, data, onSuccess, onError, timeout = 40000) {
        const verb = type === 'GET' ? 'GET' : 'POST';

        const url = new URL(window.location.origin);
        url.pathname = endpoint;
        const path = url.toString();
        const start = Date.now();
        const requestData = this.attachPageContext(data, verb);

        // Stringify metadata
        if (typeof data.metadata !== 'undefined') {
            const props = Object.keys(data.metadata);
            for (let i = 0; i < props.length; i += 1) {
                requestData.metadata[props[i]] = String(data.metadata[props[i]]);
            }

            if (verb === 'GET') {
                requestData.metadata = encodeURIComponent(
                    btoa(JSON.stringify(requestData.metadata))
                );
            }
        }

        let requestParams = data.requestParams || {};

        if (verb === 'GET') {
            requestParams = {
                ...requestData,
                ...requestParams
            };
        }

        const queryString = Object.keys(requestParams).length !== 0
            ? `?${Utilities.serialize(requestParams)}`
            : '';
        const requestBody = verb === 'GET' ? null : JSON.stringify(requestData);


        const xhr = new XMLHttpRequest();
        xhr.timeout = timeout;
        xhr.open(verb, path + queryString);
        xhr.setRequestHeader('Content-Type', 'application/json');
        xhr.setRequestHeader('Accept', 'application/json');

        xhr.onreadystatechange = () => {
            if (xhr.readyState !== XMLHttpRequest.DONE) return;
            const duration = Date.now() - start;
            const status = xhr.status;

            if (status === 200) {
                const safeResponse = xhr.response?.slice(0, 500);
                try {
                    onSuccess(JSON.parse(xhr.response));
                } catch (error) {
                    this.logger.error('Integrator response parse error', { verb, path, error });
                    onError({ message: 'Invalid JSON response', response: xhr.response, status_code: status });
                }
            } else {
                const safeResponse = xhr.response?.slice(0, 500);
                try {
                    onError(JSON.parse(xhr.response));
                } catch (error) {
                    this.logger.error('Integrator response parse error', { verb, path, error });
                    onError({ message: 'Invalid JSON response', response: xhr.response, status_code: status });
                }
            }
        };

        xhr.onerror = () => {
            const duration = Date.now() - start;
            this.logger.error('Integrator network error', { verb, path, duration });
            onError({ message: 'Network error' });
        };

        xhr.ontimeout = () => {
            const duration = Date.now() - start;
            this.logger.error('Integrator timeout', { verb, path, duration });
            onError({ message: 'Timeout' });
        };

        xhr.send(requestBody);

    }

    /**
     * Sends a request to the integrator
     *
     * @param {string}   type      Type of request (GET or POST)
     * @param {string}   endpoint  API endpoint (e.g. 'lp/init/rendered')
     * @param {Object}   data      Object of data to be sent
     * @param {function} onSuccess Callback to be called when request is successful
     * @param {function} onError   Callback for when an error occurs
     */
    makeRequestCancel(type, endpoint, data, onSuccess, onError) {
        this.logger.debug(`Integrator: preparing request to ${endpoint} with verb ${type}`);

        const xhr = new XMLHttpRequest();
        const verb = type === 'GET' ? 'GET' : 'POST';
        const path = `/${endpoint}`;
        const requestData = data;
        let requestParams = {};
        xhr.timeout = 40000;

        // Stringify metadata
        if (typeof data.metadata !== 'undefined') {
            const props = Object.keys(data.metadata);
            for (let i = 0; i < props.length; i += 1) {
                requestData.metadata[props[i]] = String(data.metadata[props[i]]);
            }

            if (verb === 'GET') {
                requestData.metadata = encodeURIComponent(
                    btoa(JSON.stringify(requestData.metadata))
                );
            }
        }

        if (requestData.requestParams) {
            requestParams = requestData.requestParams
        }

        if (verb === 'GET') {
            requestParams = {
                ...requestData,
                ...requestParams
            };
        }

        const requestBody = verb === 'GET' ? null : JSON.stringify(requestData);
        const queryString = Object.keys(requestParams).length !== 0 ? `?${Utilities.serialize(requestParams)}` : '';


        xhr.open(verb, path + queryString);
        xhr.setRequestHeader('Content-Type', 'application/json');
        xhr.setRequestHeader('Accept', 'application/json');
        xhr.onerror = () => {
            this.logger.error(`Integrator: got ERROR calling  ${verb} ${path}`, requestData, xhr.response);
            let payload = {};
            try {
                payload = JSON.parse(xhr.response);
                payload.response_string = xhr.response;
            } catch (error) {
                payload = { reference: null, status_code: xhr.status, message: 'could not parse integrator response', response_string: xhr.response, error: error };
            }
            onError(payload);
        };
        xhr.ontimeout = () => {
            this.logger.error(`Integrator: got TIMEOUT calling  ${verb} ${path}`, requestData);
            onError({ reference: null, message: 'timeout' });
        };
        xhr.onreadystatechange = () => {
            // if request is done
            if (xhr.readyState === XMLHttpRequest.DONE) {
                const xhrStatus = xhr.status;
                if (xhrStatus === 200) {
                    this.logger.debug(`Integrator: received response  ${verb} ${path}`, xhr.response);
                    onSuccess();
                } else {
                    let payload = {};
                    try {
                        payload = JSON.parse(xhr.response);
                        payload.response_string = xhr.response;
                    } catch (error) {
                        payload = { reference: null, status_code: xhrStatus, message: 'could not parse integrator response', response_string: xhr.response, error: error };
                    }
                    onError(payload);
                }
            }
        };

        xhr.send(requestBody);
    }

    /**
     * Was the user redirected to the LP from the integrator?
     */
    isRedirectRequest() {
        return (typeof this.requestData.integrator_request !== 'undefined' && this.requestData.integrator_request);
    }

    /**
     * Parse the data received from the integrator
     * (if this has been a request to a LP from the Integrator)
     */
    parseRedirectRequest() {
        let request;

        switch (this.requestData.integrator_request) {
            case IntegratorAPI.INTEGRATOR_REQUEST_SUBSCRIBE:
                if (this.requestData.integrator_status === IntegratorAPI.INTEGRATOR_STATUS_SUCCESS) {
                    request = new IntegratorAPI.SubscribeRequest(this.requestData);
                } else {
                    request = new IntegratorAPI.SubscribeErrorRequest(this.requestData);
                }
                break;

            case IntegratorAPI.INTEGRATOR_REQUEST_IDENTIFY:
                if (this.requestData.integrator_status === IntegratorAPI.INTEGRATOR_STATUS_SUCCESS) {
                    request = new IntegratorAPI.IdentifyRequest(this.requestData);
                } else {
                    request = new IntegratorAPI.IdentifyErrorRequest(this.requestData);
                }
                break;

            case IntegratorAPI.INTEGRATOR_REQUEST_USER_CANCELLED:
                if (this.requestData.integrator_status === IntegratorAPI.INTEGRATOR_STATUS_SUCCESS) {
                    request = new IntegratorAPI.UserCancelledRequest(this.requestData);
                } // We do not expect an error for user cancelled
                break;

            default:
                this.logger.error('Unknown integrator request: ', { requestData: this.requestData });
                return null;
        }

        // Validate request
        if (typeof request === 'undefined' || !request.isValid()) {
            this.logger.error('Invalid request: ', { requestData: this.requestData, parsedRequest: request });
            return null;
        }

        this.logger.debug('Integrator request parsed: ', { requestData: this.requestData, parsedRequest: request });
        return request;
    }

    /**
     *  Loads an iframe
     * @param id
     * @param endpoint
     * @param data
     */
    loadIframe(id, endpoint, data) {
        this.loadUrlIframe(id, this.baseUrl, endpoint, data);
    }

    /* eslint-disable class-methods-use-this */

    /**
     *  Loads an iframe
     * @param id
     * @param url
     * @param endpoint
     * @param data
     */
    loadUrlIframe(id, url, endpoint, data) {
        const requestData = data;
        // Stringify metadata
        if (typeof data.metadata !== 'undefined') {
            const props = Object.keys(data.metadata);
            for (let i = 0; i < props.length; i += 1) {
                requestData.metadata[props[i]] = String(data.metadata[props[i]]);
            }

            requestData.metadata = btoa(JSON.stringify(requestData.metadata));
        }

        const queryString = typeof requestData !== 'undefined' && Object.keys(requestData).length ? `?${Utilities.serialize(requestData)}` : '';
        const path = `${url}/${endpoint}${queryString}`;
        const iframeOrigin = (() => {
            try {
                return new URL(path, window.location.href).origin;
            } catch (error) {
                return null;
            }
        })();

        const existingIframe = document.getElementById(id);
        this.logger.debug('[LP Boot] identify_iframe_element', {
            iframe_id: id,
            iframe_src: path,
            iframe_origin: iframeOrigin,
            iframe_exists: existingIframe !== null,
            endpoint,
            current_url: window.location.href,
        });

        if (existingIframe === null) {
            const iframe = document.createElement('iframe');
            iframe.setAttribute('id', id);
            iframe.addEventListener('load', () => {
                this.logger.debug('identify_iframe_load', {
                    iframe_id: id,
                    iframe_src: iframe.src,
                    iframe_origin: iframeOrigin,
                });
            }, { once: true });
            iframe.addEventListener('error', () => {
                this.logger.error('identify_iframe_error', {
                    iframe_id: id,
                    iframe_src: iframe.src,
                    iframe_origin: iframeOrigin,
                });
            }, { once: true });
            iframe.src = path;
            iframe.name = id;
            document.body.appendChild(iframe);
        } else {
            const iframe = existingIframe;
            iframe.addEventListener('load', () => {
                this.logger.debug('identify_iframe_load', {
                    iframe_id: id,
                    iframe_src: iframe.src,
                    iframe_origin: iframeOrigin,
                });
            }, { once: true });
            iframe.addEventListener('error', () => {
                this.logger.error('identify_iframe_error', {
                    iframe_id: id,
                    iframe_src: iframe.src,
                    iframe_origin: iframeOrigin,
                });
            }, { once: true });
            iframe.src = path;
            iframe.name = id;
        }

        this.logger.debug('identify_iframe_navigation_start', {
            iframe_id: id,
            iframe_src: path,
            iframe_origin: iframeOrigin,
            endpoint,
        });
    }

    deleteIdentityFrame() {
        let iframe = document.getElementById('identify-frame')
        iframe.remove()
    }
}
export default Integrator;
